import {
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptRelationship,
} from 'src/containers/ProjectDiagram/types/data';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { removeModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/removeModelConceptClass';
import { removeModelConceptHierarchy } from 'src/containers/ProjectDiagram/utils/modelConceptHierarchy/removeModelConceptHierarchy';
import {
  removeModelConceptRelationship,
  removeModelConceptRelationshipInstance,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationship';
import { getProjectModelIdsForIri } from 'src/containers/ProjectDiagram/utils/projectModelIds';
import { ProjectModel } from 'src/store/state/projects';

/**
 * Checks if a relationship instance is still valid based on the refreshed model's domain/range.
 * Returns true if the instance should be disconnected (is no longer valid).
 */
const shouldDisconnectRelationshipInstance = (
  linkData: ModelConceptRelationship,
  property: { domain: string[]; range: string[] }
): boolean => {
  const fromIri = linkData.from
    ? getModelConceptClassValue(linkData.from)
    : undefined;
  const toIri = linkData.to
    ? getModelConceptClassValue(linkData.to)
    : undefined;

  const isFullyConnected = fromIri && toIri;
  const hasDomainOnly = fromIri && !toIri;
  const hasRangeOnly = !fromIri && toIri;

  if (isFullyConnected) {
    const domainValid = property.domain.includes(fromIri);
    const rangeValid = property.range.includes(toIri);
    return !domainValid || !rangeValid;
  }

  if (hasDomainOnly) {
    const remoteHasDomainOnly =
      property.domain.length > 0 && property.range.length === 0;
    const domainValid = property.domain.includes(fromIri);
    return !remoteHasDomainOnly || !domainValid;
  }

  if (hasRangeOnly) {
    const remoteHasRangeOnly =
      property.domain.length === 0 && property.range.length > 0;
    const rangeValid = property.range.includes(toIri);
    return !remoteHasRangeOnly || !rangeValid;
  }

  // Fully unconnected - valid only if remote has no domains and no ranges
  const remoteIsFullyUnconnected =
    property.domain.length === 0 && property.range.length === 0;
  return !remoteIsFullyUnconnected;
};

/**
 * Removes model concepts (classes, hierarchies, relationships) that no longer exist
 * in the refreshed project model. This handles the deletion of stale data after
 * a model refresh operation.
 */
export const removeStaleModelConcepts =
  (refreshedModel: ProjectModel, modelId: string) => (model: Model) => {
    const classesToDelete: ModelConceptClass[] = [];
    const hierarchiesToDelete: ModelConceptHierarchy[] = [];
    const relationshipsToDelete: ModelConceptRelationship[] = [];
    const relationshipInstancesToDisconnect: ModelConceptRelationship[] = [];

    // Find classes to delete
    model.nodeDataArray.forEach((nodeData) => {
      if (!isModelConceptClass(nodeData)) {
        return;
      }

      const modelIds = getProjectModelIdsForIri(nodeData.value, model);
      if (modelIds.length !== 1 || !modelIds.includes(modelId)) {
        return;
      }

      if (refreshedModel.classes && !refreshedModel.classes[nodeData.value]) {
        classesToDelete.push(nodeData);
      }
    });

    // Find hierarchies and relationships to delete
    model.linkDataArray.forEach((linkData) => {
      if (isModelConceptHierarchy(linkData)) {
        const childIri = getModelConceptClassValue(linkData.from);
        const parentIri = getModelConceptClassValue(linkData.to);

        const childModelIds = getProjectModelIdsForIri(childIri, model);
        if (childModelIds.length !== 1 || !childModelIds.includes(modelId)) {
          return;
        }

        const childClass = refreshedModel.classes?.[childIri];
        const hierarchyStillExists = childClass?.parents.includes(parentIri);

        if (!hierarchyStillExists) {
          hierarchiesToDelete.push(linkData);
        }
      } else if (isModelConceptRelationship(linkData)) {
        const modelIds = getProjectModelIdsForIri(linkData.value, model);
        if (modelIds.length !== 1 || !modelIds.includes(modelId)) {
          return;
        }

        const property = refreshedModel.properties?.[linkData.value];
        if (!property) {
          relationshipsToDelete.push(linkData);
          return;
        }

        if (shouldDisconnectRelationshipInstance(linkData, property)) {
          relationshipInstancesToDisconnect.push(linkData);
        }
      }
    });

    // Delete hierarchies first (they depend on classes)
    if (hierarchiesToDelete.length) {
      addDeletedData(hierarchiesToDelete)(model);
      hierarchiesToDelete.forEach((hierarchy) => {
        removeModelConceptHierarchy(hierarchy)(model);
      });
    }

    // Disconnect relationship instances that are no longer valid connections
    if (relationshipInstancesToDisconnect.length) {
      addDeletedData(relationshipInstancesToDisconnect)(model);
      relationshipInstancesToDisconnect.forEach((relationship) => {
        removeModelConceptRelationshipInstance(relationship)(model);
      });
    }

    // Delete relationships where the property no longer exists
    if (relationshipsToDelete.length) {
      addDeletedData(relationshipsToDelete)(model);
      relationshipsToDelete.forEach((relationship) => {
        removeModelConceptRelationship(relationship)(model);
      });
    }

    // Delete classes last
    if (classesToDelete.length) {
      addDeletedData(classesToDelete)(model);
      classesToDelete.forEach((nodeData) => {
        removeModelConceptClass(nodeData)(model);
      });
    }
  };
